import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, ArrowRight, Loader2, Info, UploadCloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const DemandeAide = () => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    sujet: '',
    description: '',
    secteur: '',
    telephone: user?.phone || '',
    localisation: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let fichier_joint = null;
      
      if (file) {
        const uploadData = new FormData();
        uploadData.append('file', file);
        const uploadRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, uploadData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        fichier_joint = uploadRes.data.path;
      }

      const payload = { ...formData, fichier_joint };
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/demandes`, payload, { withCredentials: true });
      
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-bold">Demande envoyée avec succès</span>
          <span className="text-sm">Référence : {res.data.reference}</span>
        </div>,
        { duration: 5000 }
      );
      navigate(`/recipisse/${res.data.id}`);
    } catch (e) {
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-[#0A192F] text-white p-8 relative overflow-hidden border-b-4 border-white">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full"></div>
            <div className="relative z-10">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Demander de l'aide</h1>
              <p className="text-blue-100 text-sm">
                Assistance sociale, médicale ou financière. Votre demande sera évaluée par nos services.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6" data-testid="form-demande-aide">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 text-blue-800 text-sm mb-6">
              <Info className="w-5 h-5 shrink-0 text-blue-600" />
              <p>Toutes les demandes sont traitées en toute confidentialité. Un agent prendra contact avec vous si des informations supplémentaires sont nécessaires.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Secteur concerné *</label>
                <select 
                  name="secteur" 
                  value={formData.secteur} 
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-[#1E3A8A] outline-none transition-all bg-white"
                  data-testid="input-secteur"
                >
                  <option value="">Sélectionnez un secteur...</option>
                  <option value="Santé et Médical">Santé et Médical</option>
                  <option value="Enfance et Famille">Enfance et Famille</option>
                  <option value="Éducation et Scolarité">Éducation et Scolarité</option>
                  <option value="Aide Sociale / Financière">Aide Sociale / Financière</option>
                  <option value="Handicap et Autisme">Handicap et Autisme</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Objet de la demande *</label>
                <input 
                  type="text" 
                  name="sujet"
                  value={formData.sujet}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Prise en charge des frais médicaux"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-[#1E3A8A] outline-none transition-all"
                  data-testid="input-sujet"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Description détaillée *</label>
                <textarea 
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Expliquez votre situation et le besoin exact..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-[#1E3A8A] outline-none transition-all resize-none"
                  data-testid="input-description"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Téléphone de contact *</label>
                <input 
                  type="tel" 
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  required
                  placeholder="Ex: +243..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-[#1E3A8A] outline-none transition-all"
                  data-testid="input-telephone"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Localisation / Commune *</label>
                <input 
                  type="text" 
                  name="localisation"
                  value={formData.localisation}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Kolwezi centre"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-[#1E3A8A] outline-none transition-all"
                  data-testid="input-localisation"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Pièce justificative (Optionnel)</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-slate-300 border-dashed rounded-xl hover:border-blue-900 transition-colors cursor-pointer relative bg-slate-50">
                  <div className="space-y-2 text-center">
                    <UploadCloud className="mx-auto h-8 w-8 text-slate-400" />
                    <div className="flex text-sm text-slate-600 justify-center">
                      <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-900 hover:text-blue-800 focus-within:outline-none px-2 py-1">
                        <span>Sélectionner un fichier</span>
                        <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept="image/*,.pdf,.doc,.docx" />
                      </label>
                    </div>
                    <p className="text-xs text-slate-500">PNG, JPG, PDF jusqu'à 10MB</p>
                    {file && (
                      <p className="text-sm font-bold text-emerald-600 mt-2 bg-emerald-50 py-1 px-2 rounded inline-block">
                        Fichier sélectionné : {file.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100 flex justify-end">
              <Button 
                type="button" 
                variant="ghost" 
                className="mr-3 text-slate-500 hover:text-slate-700"
                onClick={() => navigate('/citoyen')}
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-[#0A192F] hover:bg-[#1E3A8A] text-white px-8 h-12 rounded-xl"
                data-testid="submit-demande-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>Envoyer la demande <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DemandeAide;
